import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, Home as HomeIcon, Keyboard, Presentation,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight,
} from "lucide-react";
import { fetchSchool } from "@/lib/api";
import { getSchoolMemories, addSchoolMemory } from "@/lib/memories";
import { useKeyboardMovement } from "@/hooks/useKeyboardMovement";
import { Avatar } from "@/components/game/Avatar";
import { ClassroomDecor } from "@/components/school/ClassroomDecor";
import { SchoolObject } from "@/components/school/SchoolObject";
import { SchoolItemCard } from "@/components/school/SchoolItemCard";
import { BlackboardModal, BellModal } from "@/components/school/SpecialModals";
import { BagOpenModal } from "@/components/school/BagOpenModal";
import { TiffinOpenModal } from "@/components/school/TiffinOpenModal";
import { AmbientMusic } from "@/components/AmbientMusic";
import { MoveHint } from "@/components/game/MoveHint";
import { useIsTouch } from "@/hooks/useIsTouch";
import { SchoolCollectionPanel } from "@/components/SchoolCollectionPanel";
import { GameArcade } from "@/components/games/GameArcade";

const BOUNDS = { minX: 5, maxX: 95, minY: 30, maxY: 90 };
const THRESHOLD = 10;
const START = { x: 50, y: 86 };

const ZONES = [
  { id: "blackboard", kind: "blackboard", label: "Blackboard", sub: "Padho / Likho", sprite: "slate", x: 37, y: 52 },
  { id: "bell", kind: "bell", label: "School Ghanti", sub: "BAJAAO!", sprite: "bell", x: 85, y: 45 },
  { id: "bag", kind: "bag", label: "School Bag", sub: "Andar kya hai?", sprite: "bag", x: 16, y: 64 },
  { id: "tiffin", kind: "tiffin", label: "Tiffin Box", sub: "Kya laaye?", sprite: "tiffin", x: 64, y: 50 },
  { id: "games", kind: "games-grid", label: "Classroom Games", sub: "Khelo?", sprite: "notebook", x: 86, y: 74 },
  { id: "home", kind: "home", label: "Nostalgic World", sub: "Wapas jao", sprite: "globe", x: 9, y: 82 },
];

export default function SchoolClassroom() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [memories, setMemories] = useState(getSchoolMemories());
  const [selected, setSelected] = useState(null);
  const [grid, setGrid] = useState(null); // 'stationery' | 'game'
  const [special, setSpecial] = useState(null); // 'blackboard' | 'bell'
  const [bagOpen, setBagOpen] = useState(false);
  const [tiffinOpen, setTiffinOpen] = useState(false);
  const [collection, setCollection] = useState(false);
  const [arcadeOpen, setArcadeOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const [boardText, setBoardText] = useState("GOOD MORNING STUDENTS");

  const anyOverlay = !entered || !!selected || !!grid || !!special || bagOpen || tiffinOpen || collection || arcadeOpen;
  const isTouch = useIsTouch();
  const { pos, setPos, facing, moving, moveTo } = useKeyboardMovement({ initial: START, bounds: BOUNDS, enabled: !anyOverlay });

  const tapToWalk = (e) => {
    if (anyOverlay) return;
    const r = e.currentTarget.getBoundingClientRect();
    moveTo(((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
  };

  useEffect(() => {
    fetchSchool().then(setItems).catch(() => toast.error("Classroom failed to load"));
  }, []);

  const byName = useMemo(() => {
    const m = {};
    items.forEach((i) => (m[i.name] = i));
    return m;
  }, [items]);

  const objectFor = (kind) => {
    const map = {
      bag: "School Bag", geometry: "Geometry Box", tiffin: "Lunch Box / Tiffin",
      notebook: "Covered Notebook", blackboard: "Blackboard", bell: "School Bell",
    };
    return byName[map[kind]];
  };

  const active = useMemo(() => {
    let best = null, bestD = THRESHOLD;
    for (const z of ZONES) {
      const d = Math.hypot(z.x - pos.x, z.y - pos.y);
      if (d < bestD) { bestD = d; best = z; }
    }
    return best;
  }, [pos]);

  const addMem = (id) => { setMemories(addSchoolMemory(id)); toast.success("+1 School Memory ❤️"); };

  const interact = (z) => {
    if (!z) return;
    if (z.kind === "home") return navigate("/");
    if (z.kind === "stationery-grid") return setGrid("stationery");
    if (z.kind === "games-grid") return setArcadeOpen(true);
    if (z.kind === "blackboard") return setSpecial("blackboard");
    if (z.kind === "bell") return setSpecial("bell");
    if (z.kind === "bag") return setBagOpen(true);
    if (z.kind === "tiffin") return setTiffinOpen(true);
    const obj = objectFor(z.kind);
    if (obj) setSelected(obj);
  };

  useEffect(() => {
    const h = (e) => { if (e.key === "Enter" && !anyOverlay && active) { e.preventDefault(); interact(active); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyOverlay, active]);

  const holdRef = useRef(null);
  const nudge = (dx, dy) => setPos((p) => ({ x: Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, p.x + dx)), y: Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, p.y + dy)) }));
  const startHold = (dx, dy) => { if (anyOverlay) return; nudge(dx, dy); holdRef.current = setInterval(() => nudge(dx, dy), 40); };
  const stopHold = () => holdRef.current && clearInterval(holdRef.current);

  const gridItems = grid ? items.filter((i) => i.category === grid) : [];

  useEffect(() => {
    if (entered) return;
    const h = (e) => { if (e.key === "Enter") { e.preventDefault(); setEntered(true); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [entered]);

  return (
    <div className="min-h-screen bg-background paper-grain">
      <AmbientMusic src="/audio/school_bg.mp3" />

      {/* School department cover / intro screen */}
      {!entered && (
        <div data-testid="school-intro" className="fixed inset-0 z-[120] flex items-center justify-center overflow-hidden"
          style={{ background: "radial-gradient(circle at 50% 40%, #2F4F3A, #1c2f24)" }}>
          <div className="pointer-events-none absolute inset-0 paper-grain opacity-30" />
          <div className="relative mx-4 max-w-lg rounded-2xl border-[4px] border-kirana-gold bg-[#20361F]/70 p-8 text-center backdrop-blur-sm hard-shadow animate-card-in">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-kirana-gold bg-[#2F4F3A]">
              <Presentation className="h-8 w-8 text-kirana-gold" strokeWidth={2} />
            </div>
            <h1 className="font-signboard text-5xl font-bold leading-none text-kirana-cream drop-shadow">School Days</h1>
            <p className="font-signboard mt-1 text-2xl font-bold text-kirana-gold">विद्यालय</p>
            <p className="mx-auto mt-3 max-w-sm text-sm text-kirana-cream/80">
              Chalk ki khushboo, ghanti ka intezaar, tiffin ka countdown… step into an Indian classroom of 1995–2005.
            </p>
            <button data-testid="school-enter-btn" onClick={() => setEntered(true)}
              className="mt-6 inline-flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-gold px-6 py-3 font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow">
              <Keyboard className="h-5 w-5" /> Press ENTER to go in
            </button>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-40 border-b-[3px] border-kirana-brown bg-[#2F4F3A] text-kirana-cream">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5">
          <div>
            <h1 className="font-signboard text-2xl font-bold leading-none sm:text-3xl">School · विद्यालय</h1>
            <p className="text-[11px] text-kirana-cream/80">An Indian Classroom · 1995–2005</p>
          </div>
          <div className="flex items-center gap-2">
            <div data-testid="school-hud-count" className="hidden items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-black/20 px-3 py-1.5 text-sm font-bold sm:flex">
              <Heart className="h-4 w-4 fill-kirana-cream" /> {memories.length} / {items.length || 51}
            </div>
            <button data-testid="open-school-memories" onClick={() => setCollection(true)}
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-brown bg-kirana-gold px-3 py-1.5 text-sm font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm">
              <Heart className="h-4 w-4" /> <span className="hidden sm:inline">My Memories</span>
            </button>
            <Link data-testid="open-home" to="/" className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold transition-colors hover:bg-black/20">
              <HomeIcon className="h-4 w-4" /> <span className="hidden sm:inline">Home</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-3 py-4">
        <div className="mb-2 flex items-center justify-end">
          <MoveHint verb="interact" />
        </div>

        <div data-testid="classroom-area" className="play-surface relative aspect-[1264/848] w-full overflow-hidden rounded-xl border-[4px] border-kirana-brown hard-shadow">
          <ClassroomDecor boardText={boardText} />

          {/* tap-to-move floor — below zones (z-20), above decor (z-10) */}
          <div data-testid="floor-tap" className="absolute inset-0 z-[15] cursor-pointer" onClick={tapToWalk} />

          {ZONES.map((z) => {
            const obj = objectFor(z.kind);
            const unlocked = obj && memories.includes(obj.id);
            return (
              <SchoolObject key={z.id} zone={z} active={active?.id === z.id} unlocked={unlocked} onClick={() => interact(z)} />
            );
          })}

          <Avatar pos={pos} facing={facing} moving={moving} />
        </div>

        {/* touch controls — below the scene so they never cover a card */}
        <div className={`touch-pad mx-auto mt-3 w-max grid-cols-3 gap-1.5 ${isTouch ? "grid" : "hidden"}`}>
          <span />
          <DPad onDown={() => startHold(0, -1.5)} onUp={stopHold} icon={ArrowUp} tid="dpad-up" />
          <span />
          <DPad onDown={() => startHold(-1.5, 0)} onUp={stopHold} icon={ArrowLeft} tid="dpad-left" />
          <DPad onDown={() => active && interact(active)} onUp={() => {}} label="⏎" tid="dpad-enter" />
          <DPad onDown={() => startHold(1.5, 0)} onUp={stopHold} icon={ArrowRight} tid="dpad-right" />
          <span />
          <DPad onDown={() => startHold(0, 1.5)} onUp={stopHold} icon={ArrowDown} tid="dpad-down" />
          <span />
        </div>
      </main>

      {/* grid panel (games) */}
      {grid && (
        <SchoolCollectionPanel
          open={!!grid}
          onClose={() => setGrid(null)}
          items={gridItems}
          memories={memories}
          onSelect={(i) => setSelected(i)}
          simple
          title="Classroom Games"
        />
      )}

      {/* special modals */}
      <BlackboardModal open={special === "blackboard"} onClose={() => setSpecial(null)}
        value={boardText} onChange={setBoardText}
        itemId={objectFor("blackboard")?.id} unlocked={memories.includes(objectFor("blackboard")?.id)} onAdd={addMem} />
      <BellModal open={special === "bell"} onClose={() => setSpecial(null)}
        itemId={objectFor("bell")?.id} unlocked={memories.includes(objectFor("bell")?.id)} onAdd={addMem} />

      {/* interactive school bag */}
      <BagOpenModal open={bagOpen} onClose={() => setBagOpen(false)}
        itemId={objectFor("bag")?.id} unlocked={memories.includes(objectFor("bag")?.id)} onAdd={addMem} />

      {/* interactive tiffin */}
      <TiffinOpenModal open={tiffinOpen} onClose={() => setTiffinOpen(false)}
        itemId={objectFor("tiffin")?.id} unlocked={memories.includes(objectFor("tiffin")?.id)} onAdd={addMem} />

      {/* full collection */}
      <SchoolCollectionPanel open={collection} onClose={() => setCollection(false)}
        items={items} memories={memories} onSelect={(i) => setSelected(i)} />

      {/* playable games arcade */}
      {arcadeOpen && <GameArcade open={arcadeOpen} onClose={() => setArcadeOpen(false)} groups={["Classroom", "Timepass"]} title="Classroom Games" subtitle="Back-bench time-pass — tap a game to play" />}

      {/* item card — rendered last so it always layers above the grid/collection panels */}
      <SchoolItemCard open={!!selected} item={selected} onClose={() => setSelected(null)}
        unlocked={selected ? memories.includes(selected.id) : false} onAdd={addMem} />
    </div>
  );
}

const DPad = ({ onDown, onUp, icon: Icon, label, tid }) => (
  <button data-testid={tid} onPointerDown={(e) => { e.preventDefault(); onDown(); }} onPointerUp={onUp} onPointerLeave={onUp} onContextMenu={(e) => e.preventDefault()}
    className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-kirana-brown bg-kirana-cream/90 text-kirana-brown hard-shadow-sm">
    {Icon ? <Icon className="h-5 w-5" strokeWidth={3} /> : <span className="font-bold">{label}</span>}
  </button>
);
